// ===== Venue Dashboard Context with Backend Integration =====
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const VenueDashboardContext = createContext(null);

// API Base URL from environment
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Default venue ID for testing - Chalong Interview Condominium
const DEFAULT_VENUE_ID = '692a97a595535df3cda97009';

// Default street vendor - Ali's Morning Coffee Cart
const DEFAULT_VENDOR_ID = '4889157f';

// Available roles for testing (in dev mode, user can switch)
const AVAILABLE_ROLES = ['ADMIN', 'OWNER', 'MANAGER', 'STAFF', 'MEMBER'];

// Mock user for dev mode - set as MANAGER of the venue
const DEV_USER = {
  _id: 'dev-manager-001',
  name: 'Dev Manager',
  email: 'manager@honestlee.app',
  phone: '+66987654321',
  role: 'MANAGER',
  loginMethod: 'dev-bypass'
};

export const useVenueDashboard = () => {
  const context = useContext(VenueDashboardContext);
  if (!context) {
    throw new Error('useVenueDashboard must be used within VenueDashboardProvider');
  }
  return context;
};

export const VenueDashboardProvider = ({ children }) => {
  // State
  const [currentRole, setCurrentRole] = useState(() => {
    return localStorage.getItem('venue_dashboard_role') || 'MANAGER';
  });
  const [user, setUser] = useState(DEV_USER);
  const [venue, setVenue] = useState(null);
  const [streetVendor, setStreetVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data state
  const [offers, setOffers] = useState([]);
  const [events, setEvents] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [staff, setStaff] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [wifiTests, setWifiTests] = useState([]);
  const [vitals, setVitals] = useState(null);

  // Persist role selection
  useEffect(() => {
    localStorage.setItem('venue_dashboard_role', currentRole);
    // Update user role when switched
    setUser(prev => ({ ...prev, role: currentRole }));
  }, [currentRole]);

  // Fetch venue data from backend
  const fetchVenue = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/venues/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th' // Thailand region
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch venue: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Venue data fetched:', data);
      
      // Handle different response structures
      const venueData = data.data || data.venue || data;
      setVenue(venueData);
      return venueData;
    } catch (err) {
      console.error('❌ Error fetching venue:', err);
      // Fallback to mock data on error
      setVenue({
        _id: venueId,
        id: venueId,
        name: 'Chalong Interview Condominium',
        category: 'Accommodation',
        venuecategorydisplayname: 'Accommodation',
        city: 'Phuket',
        _fallback: true
      });
      return null;
    }
  }, []);

  // Fetch street vendor data
  const fetchStreetVendor = useCallback(async (vendorId = DEFAULT_VENDOR_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/street-vendors`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch vendors: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Street vendors fetched:', data);
      
      // Find Ali's vendor by ID or name
      const vendors = data.data || data.vendors || data || [];
      const aliVendor = Array.isArray(vendors) 
        ? vendors.find(v => v._id?.includes(vendorId) || v.vendorName?.includes("Ali"))
        : null;
      
      if (aliVendor) {
        setStreetVendor(aliVendor);
        return aliVendor;
      }
      
      // Fallback mock data
      setStreetVendor({
        _id: vendorId,
        vendorName: "Ali's Morning Coffee Cart",
        vendorType: 'Food Cart',
        cuisineTags: 'Coffee;Beverages',
        paymentTypes: 'Cash',
        priceRange: '$',
        isOperational: true,
        _fallback: true
      });
      return null;
    } catch (err) {
      console.error('❌ Error fetching street vendor:', err);
      setStreetVendor({
        _id: vendorId,
        vendorName: "Ali's Morning Coffee Cart",
        vendorType: 'Food Cart',
        cuisineTags: 'Coffee;Beverages',
        paymentTypes: 'Cash',
        priceRange: '$',
        isOperational: true,
        _fallback: true
      });
      return null;
    }
  }, []);

  // Fetch offers for venue
  const fetchOffers = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/offers/venue/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const offersList = data.data || data.offers || data || [];
        setOffers(Array.isArray(offersList) ? offersList : []);
        console.log('✅ Offers fetched:', offersList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching offers:', err);
      setOffers([]);
    }
  }, []);

  // Fetch events for venue
  const fetchEvents = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/events/venue/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const eventsList = data.data || data.events || data || [];
        setEvents(Array.isArray(eventsList) ? eventsList : []);
        console.log('✅ Events fetched:', eventsList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching events:', err);
      setEvents([]);
    }
  }, []);

  // Fetch reviews for venue
  const fetchReviews = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/reviews/venue/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const reviewsList = data.data || data.reviews || data || [];
        setReviews(Array.isArray(reviewsList) ? reviewsList : []);
        console.log('✅ Reviews fetched:', reviewsList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching reviews:', err);
      setReviews([]);
    }
  }, []);

  // Fetch staff roster for venue
  const fetchStaff = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/staff/roster/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const staffList = data.data || data.roster || data || [];
        setStaff(Array.isArray(staffList) ? staffList : []);
        console.log('✅ Staff fetched:', staffList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching staff:', err);
      setStaff([]);
    }
  }, []);

  // Fetch redemptions for venue
  const fetchRedemptions = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/redemptions/venue/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const redemptionsList = data.data || data.redemptions || data || [];
        setRedemptions(Array.isArray(redemptionsList) ? redemptionsList : []);
        console.log('✅ Redemptions fetched:', redemptionsList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching redemptions:', err);
      setRedemptions([]);
    }
  }, []);

  // Fetch WiFi speed tests for venue
  const fetchWifiTests = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/wifi-speed/venue/${venueId}`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const testsList = data.data || data.tests || data || [];
        setWifiTests(Array.isArray(testsList) ? testsList : []);
        console.log('✅ WiFi tests fetched:', testsList.length);
      }
    } catch (err) {
      console.error('❌ Error fetching WiFi tests:', err);
      setWifiTests([]);
    }
  }, []);

  // Fetch venue vitals
  const fetchVitals = useCallback(async (venueId = DEFAULT_VENUE_ID) => {
    try {
      const response = await fetch(`${API_BASE}/api/venues/${venueId}/vitals`, {
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setVitals(data.data || data.vitals || data);
        console.log('✅ Vitals fetched');
      }
    } catch (err) {
      console.error('❌ Error fetching vitals:', err);
      setVitals(null);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all data in parallel
        await Promise.all([
          fetchVenue(),
          fetchStreetVendor(),
          fetchOffers(),
          fetchEvents(),
          fetchReviews(),
          fetchStaff(),
          fetchRedemptions(),
          fetchWifiTests(),
          fetchVitals()
        ]);
        
        console.log('✅ All dashboard data loaded');
      } catch (err) {
        console.error('❌ Error loading dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [fetchVenue, fetchStreetVendor, fetchOffers, fetchEvents, fetchReviews, fetchStaff, fetchRedemptions, fetchWifiTests, fetchVitals]);

  // Switch role function for the role switcher
  const switchRole = (newRole) => {
    if (AVAILABLE_ROLES.includes(newRole)) {
      console.log(`🔄 Switching role from ${currentRole} to ${newRole}`);
      setCurrentRole(newRole);
    }
  };

  // Check if user can access based on allowed roles
  const canAccess = (allowedRoles = []) => {
    if (allowedRoles.length === 0) return true;
    return allowedRoles.includes(currentRole);
  };

  // Refresh all data
  const refreshData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchVenue(),
      fetchStreetVendor(),
      fetchOffers(),
      fetchEvents(),
      fetchReviews(),
      fetchStaff(),
      fetchRedemptions(),
      fetchWifiTests(),
      fetchVitals()
    ]);
    setLoading(false);
  }, [fetchVenue, fetchStreetVendor, fetchOffers, fetchEvents, fetchReviews, fetchStaff, fetchRedemptions, fetchWifiTests, fetchVitals]);

  // Toggle vendor operational status
  const toggleVendorOperational = useCallback(async (vendorId, isOperational) => {
    try {
      const response = await fetch(`${API_BASE}/api/street-vendors/${vendorId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-region': 'th'
        },
        credentials: 'include',
        body: JSON.stringify({ isOperational })
      });
      
      if (response.ok) {
        setStreetVendor(prev => ({ ...prev, isOperational }));
        console.log('✅ Vendor status updated:', isOperational);
        return true;
      }
      return false;
    } catch (err) {
      console.error('❌ Error updating vendor status:', err);
      // Update locally anyway for UI responsiveness
      setStreetVendor(prev => ({ ...prev, isOperational }));
      return false;
    }
  }, []);

  const value = {
    // Auth state
    user,
    venue,
    streetVendor,
    loading,
    error,
    isAuthenticated: true, // Dev mode bypass
    
    // Dashboard data
    offers,
    events,
    reviews,
    staff,
    redemptions,
    wifiTests,
    vitals,
    
    // Role management
    currentRole,
    availableRoles: AVAILABLE_ROLES,
    switchRole,
    canAccess,
    
    // Actions
    refreshData,
    toggleVendorOperational,
    
    // Individual data setters (for optimistic updates)
    setVenue,
    setStreetVendor,
    setOffers,
    setEvents,
    setReviews,
    setStaff,
    setRedemptions,
    
    // Venue ID for API calls
    venueId: venue?._id || venue?.id || DEFAULT_VENUE_ID,
    vendorId: streetVendor?._id || DEFAULT_VENDOR_ID
  };

  return (
    <VenueDashboardContext.Provider value={value}>
      {children}
    </VenueDashboardContext.Provider>
  );
};

export default VenueDashboardContext;
